import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Checkbox,
  Divider,
  Flex,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons'
import {
  AntibodyScreenResult,
  EncounterStatus,
  EncounterType,
  getDateString,
  labsBloodTypeSectionField,
  labsGbsSectionField,
  labsGlucoseSectionField,
  labsNstBppSectionField,
  labsOrderedSectionField,
  labsOtherSectionField,
  labsRhogamSectionField,
  NstResult,
  RhFactor,
  UltrasoundData,
} from '@hb/shared'
import { useCallback, useEffect } from 'react'
import { useField, useForm } from 'react-final-form'
import { FormElement } from '../../../../components/forms/Input'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useAssessmentExams, useAssessmentLabResults } from '../../../../hooks/backend'
import { useGbsConfig } from '../../../../hooks/backend/gbs/useGbsConfig'
import { LabQuickview } from '../../../Pregnancy/LaborCharting/LabQuickview'
import { SectionLabel } from '../../SectionLabel'

const STUDY_TYPE_LABELS: Record<string, string> = {
  'limited-pocus': 'Limited POCUS',
  'standard-ob': 'Standard OB',
  detailed: 'Detailed',
  bpp: 'BPP',
  'nst-us': 'NST + Ultrasound',
  transvaginal: 'Transvaginal',
}

const formatGa = (days: number) => `${Math.floor(days / 7)}w ${days % 7}d`

const UltrasoundSummary = ({ assessmentId }: { assessmentId: string }) => {
  const { dataArr: ultrasounds, loading } = useAssessmentExams(
    assessmentId,
    EncounterType.ULTRASOUND,
  )
  const count = ultrasounds?.length ?? 0

  return (
    <Box mb={3}>
      <Flex align="center" gap={3} mb={2}>
        <Text fontSize="sm" fontWeight={600} color="gray.600">
          Ultrasound Studies
        </Text>
        {loading ? (
          <Spinner size="xs" />
        ) : (
          <Badge colorScheme={count > 0 ? 'teal' : 'gray'} fontSize="xs">
            {count > 0
              ? `${count} study${count !== 1 ? ' studies' : ''} on file`
              : 'No ultrasound studies on file'}
          </Badge>
        )}
      </Flex>
      {!loading && count > 0 && (
        <Flex direction="column" gap={2}>
          {(ultrasounds ?? []).map(enc => {
            const d = enc.data as UltrasoundData | undefined
            const efw = d?.fetalBiometry?.efw
            return (
              <Flex
                key={enc.id}
                align="center"
                gap={2}
                flexWrap="wrap"
                bg="gray.50"
                _dark={{ bg: 'gray.800' }}
                borderRadius="md"
                px={3}
                py={2}
                fontSize="sm">
                <Text fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }}>
                  {enc.date ? getDateString(enc.date, 'short', true) : '—'}
                </Text>
                {d?.gaAtStudyDays != null && (
                  <Badge colorScheme="blue" fontSize="xs">
                    {formatGa(d.gaAtStudyDays)}
                  </Badge>
                )}
                {d?.studyType && (
                  <Badge colorScheme="teal" fontSize="xs">
                    {STUDY_TYPE_LABELS[d.studyType] ?? d.studyType}
                  </Badge>
                )}
                {efw != null && (
                  <Badge colorScheme="purple" fontSize="xs">
                    EFW {efw} g
                  </Badge>
                )}
                <Badge
                  colorScheme={enc.status === EncounterStatus.AUTHENTICATED ? 'green' : 'gray'}
                  fontSize="xs">
                  {enc.status === EncounterStatus.AUTHENTICATED ? 'Authenticated' : 'Draft'}
                </Badge>
              </Flex>
            )
          })}
        </Flex>
      )}
    </Box>
  )
}

export const LabsInput = ({ assessmentId }: { assessmentId?: string | null }) => {
  const {
    input: { value: gestationalAge },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })

  const {
    input: { value: gbsStatus },
  } = useField<string | undefined>('data.labs.gbsStatus', { subscription: { value: true } })

  const {
    input: { value: gctValue },
  } = useField<number | undefined>('data.labs.gctValueMgdl', { subscription: { value: true } })

  const {
    input: { value: gdmDiagnosed },
  } = useField<boolean | undefined>('data.labs.gdmDiagnosed', { subscription: { value: true } })

  const {
    input: { value: labReviewConfirmation, onChange: onReviewChange },
  } = useField<boolean | undefined>('data.labs.labReviewConfirmation', {
    subscription: { value: true },
  })

  const form = useForm()

  const {
    input: { value: labOpenedAt },
  } = useField<number | undefined>('data.labs.labReviewOpenedAt', { subscription: { value: true } })

  // Record the timestamp when this section first mounts so we can estimate review duration.
  useEffect(() => {
    if (!labOpenedAt) {
      form.change('data.labs.labReviewOpenedAt', Date.now())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirmReview = useCallback(
    (checked: boolean) => {
      onReviewChange(checked || undefined)
      // Auto-suggest elapsed time only on first confirmation, not on uncheck.
      if (checked && !labReviewConfirmation && labOpenedAt) {
        const elapsedMinutes = Math.floor((Date.now() - labOpenedAt) / 60000)
        if (elapsedMinutes > 0) {
          form.change('data.timeWorkLog.labReviewMinutes', elapsedMinutes)
        }
      }
    },
    [onReviewChange, form, labOpenedAt, labReviewConfirmation],
  )

  const {
    input: { value: rhFactor },
  } = useField<RhFactor | undefined>('data.labs.bloodTypeRh', { subscription: { value: true } })

  const {
    input: { value: antibodyResult },
  } = useField<AntibodyScreenResult | undefined>('data.labs.antibodyScreenResult', {
    subscription: { value: true },
  })

  const {
    input: { value: nstResult },
  } = useField<NstResult | undefined>('data.labs.nstBpp.result', { subscription: { value: true } })

  const {
    input: { value: bppScore },
  } = useField<number | undefined>('data.labs.nstBpp.bppScore', { subscription: { value: true } })

  const { userId: patientId } = useProfile()
  const { dataArr: labResults, loading: labsLoading } = useAssessmentLabResults(
    patientId,
    assessmentId ?? null,
  )
  const { isOpen: isLabOpen, onOpen: openLab, onClose: closeLab } = useDisclosure()
  const labCount = labResults?.length ?? 0

  // §J (Addendum June 2026): GBS window corrected to 35–37w.
  // gbsGaTriggerDays and gbsWindowEndDays come from Firestore (settings/gbsConfig),
  // defaulting to 245 (35w0d) and 265 (37w+6d) respectively.
  const { gbsGaTriggerDays, gbsWindowEndDays } = useGbsConfig()
  // GBS field is hidden entirely before 35w — does not appear at all.
  const showGbsField = typeof gestationalAge === 'number' && gestationalAge >= gbsGaTriggerDays
  // Badge fires only while inside the 35–37w collection window.
  const isGbsWindow =
    typeof gestationalAge === 'number' &&
    gestationalAge >= gbsGaTriggerDays &&
    gestationalAge <= gbsWindowEndDays

  // 28w = 196 days
  const isAntibodyScreenWindow = typeof gestationalAge === 'number' && gestationalAge >= 196

  return (
    <Box data-child-name="data.labs" w="100%" px={4} py={3}>
      <SectionLabel mb={1}>Labs &amp; Diagnostics</SectionLabel>

      {/* Labs ordered this visit */}
      <FormElement root showName={false} name="data.labs" field={labsOrderedSectionField} />

      <Divider my={3} />

      {/* Lab results review */}
      <Box mb={3}>
        <Flex align="center" gap={3} mb={2}>
          <Text fontSize="sm" fontWeight={600} color="gray.600">
            Lab Results Review
          </Text>
          {assessmentId ? (
            labsLoading ? (
              <Spinner size="xs" />
            ) : (
              <Badge colorScheme={labCount > 0 ? 'blue' : 'gray'} fontSize="xs">
                {labCount > 0
                  ? `${labCount} result${labCount !== 1 ? 's' : ''} on file`
                  : 'No lab results on file'}
              </Badge>
            )
          ) : null}
          {assessmentId && (
            <RoundedButton size="xs" variant="outline" onClick={openLab}>
              View Lab Results
            </RoundedButton>
          )}
        </Flex>
        {!assessmentId && (
          <Text fontSize="sm" color="gray.400" mb={2}>
            No lab results on file. When results are available, review and confirm below.
          </Text>
        )}
        <Checkbox
          isChecked={!!labReviewConfirmation}
          onChange={e => handleConfirmReview(e.target.checked)}
          colorScheme="green"
          size="sm">
          <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }}>
            Lab results reviewed and acknowledged
          </Text>
        </Checkbox>
        {assessmentId && patientId && (
          <LabQuickview
            assessmentId={assessmentId}
            patientId={patientId}
            isOpen={isLabOpen}
            onClose={closeLab}
          />
        )}
      </Box>

      <Divider my={3} />

      {/* §2.7.1: Blood Type & Rh Status */}
      <Box mb={3}>
        <FormElement showName name="data.labs" field={labsBloodTypeSectionField} />
        {rhFactor === 'negative' && (
          <Flex direction="column" gap={2} mt={2}>
            <Alert status="warning" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                Rh-negative patient — RhoGAM protocol applies. Antibody screen required at ≥28w.
              </AlertDescription>
            </Alert>
            {isAntibodyScreenWindow && (
              <Badge colorScheme="orange" alignSelf="flex-start" fontSize="xs">
                Rh antibody screen required (≥28w GA)
              </Badge>
            )}
            {antibodyResult && antibodyResult !== 'negative' && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <AlertDescription>
                  Antibody screen{' '}
                  {antibodyResult === 'weakly-positive' ? 'weakly positive' : 'positive'} —
                  sensitized patient. MFM consult recommended.
                </AlertDescription>
              </Alert>
            )}
            <Box mt={1}>
              <FormElement root showName name="data.labs" field={labsRhogamSectionField} />
            </Box>
          </Flex>
        )}
      </Box>

      {/* §2.7 + §J: GBS status — hidden entirely before 35w (gbsGaTriggerDays) */}
      {showGbsField && (
        <>
          <Divider my={3} />
          <Box mb={3}>
            {isGbsWindow && (
              <Badge colorScheme="orange" mb={2} fontSize="xs">
                GBS culture required this visit (35–37w GA)
              </Badge>
            )}
            <FormElement root showName name="data.labs" field={labsGbsSectionField} />
            {gbsStatus === 'positive' && (
              <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
                <AlertIcon />
                GBS positive — intrapartum antibiotic prophylaxis indicated. Document in plan.
              </Alert>
            )}
          </Box>
        </>
      )}

      <Divider my={3} />

      {/* Glucose screen (GCT) / GTT */}
      <Box mb={3}>
        <FormElement showName name="data.labs" field={labsGlucoseSectionField} />
        {typeof gctValue === 'number' && gctValue >= 130 && (
          <Alert status="info" mt={2} borderRadius="md" fontSize="sm">
            <AlertIcon />
            GCT result ≥ 130 mg/dL — consider ordering 3-hr GTT.
          </Alert>
        )}
        {gdmDiagnosed && (
          <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
            <AlertIcon />
            GDM diagnosis — care plan documentation required.
          </Alert>
        )}
      </Box>

      <Divider my={3} />

      {/* §2.7: NST / BPP result entry */}
      <Box mb={3}>
        <FormElement showName name="data.labs" field={labsNstBppSectionField} />
        {nstResult === 'non-reactive' && (
          <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
            <AlertIcon />
            Non-reactive NST — consider BPP or extended monitoring.
          </Alert>
        )}
        {typeof bppScore === 'number' && bppScore <= 4 && (
          <Alert status="error" mt={2} borderRadius="md" fontSize="sm">
            <AlertIcon />
            BPP score ≤4 — urgent clinical review indicated.
          </Alert>
        )}
        {typeof bppScore === 'number' && bppScore === 6 && (
          <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
            <AlertIcon />
            BPP score 6 — equivocal; consider repeat in 24 hours.
          </Alert>
        )}
      </Box>

      <Divider my={3} />

      {/* §2.7: Ultrasound studies summary */}
      {assessmentId && (
        <>
          <UltrasoundSummary assessmentId={assessmentId} />
          <Divider my={3} />
        </>
      )}

      {/* Other labs */}
      <Flex w="100%">
        <FormElement showName={false} name="data.labs" field={labsOtherSectionField} />
      </Flex>
    </Box>
  )
}
